import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ClipboardCheck, Clock, User, ChevronDown, ChevronUp,
  Loader2, Camera, CheckCircle2, FileText, AlertTriangle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SchichtProtokoll = () => {
  const [protokolle, setProtokolle] = useState([]);
  const [schichten, setSchichten] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  // Filter
  const [filterLocation, setFilterLocation] = useState('all');
  const [filterSchicht, setFilterSchicht] = useState('all');
  const [filterDatumVon, setFilterDatumVon] = useState('');
  const [filterDatumBis, setFilterDatumBis] = useState('');

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  useEffect(() => {
    fetchLocationsAndSchichten();
  }, []);

  useEffect(() => {
    fetchProtokolle();
  }, [filterLocation, filterSchicht, filterDatumVon, filterDatumBis]);

  const fetchLocationsAndSchichten = async () => {
    try {
      const [locsRes] = await Promise.all([
        axios.get(`${API}/user/locations`, { headers }),
      ]);
      const locs = locsRes.data || [];
      setLocations(locs);

      const schichtenResults = await Promise.all(
        locs.map(l => axios.get(`${API}/tasks/schichten?location_id=${l.id}`, { headers }).catch(() => ({ data: [] })))
      );
      const all = schichtenResults.flatMap(r => r.data || []);
      setSchichten(Array.from(new Map(all.map(s => [s.id, s])).values()));
    } catch { toast.error('Daten konnten nicht geladen werden'); }
  };

  const fetchProtokolle = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterLocation !== 'all') params.append('location_id', filterLocation);
      if (filterSchicht !== 'all') params.append('schicht_config_id', filterSchicht);
      if (filterDatumVon) params.append('datum_von', filterDatumVon);
      if (filterDatumBis) params.append('datum_bis', filterDatumBis);

      const r = await axios.get(`${API}/tasks/schicht-protokolle?${params}`, { headers });
      setProtokolle(r.data || []);
    } catch { toast.error('Protokolle konnten nicht geladen werden'); }
    finally { setLoading(false); }
  };

  const formatTime = (isoStr) => {
    if (!isoStr) return '–';
    try { return new Date(isoStr).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }); }
    catch { return '–'; }
  };

  const formatDate = (str) => {
    if (!str) return '–';
    try {
      return new Date(str + 'T00:00:00').toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', year: '2-digit' });
    } catch { return str; }
  };

  const STATUS_STYLES = {
    beendet: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
    bestaetigt: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  };
  const STATUS_LABELS = { beendet: 'Übergeben', bestaetigt: 'Übernommen' };

  return (
    <DashboardLayout>
      <div className="p-4 md:p-6 max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <ClipboardCheck className="h-6 w-6 text-primary" />
            Schicht-Protokolle
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Alle Übergabe-Berichte im Überblick – lückenlose Verantwortungskette.
          </p>
        </div>

        {/* Filter */}
        <Card className="shadow-sm">
          <CardContent className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {locations.length > 1 && (
                <div className="space-y-1 col-span-2 md:col-span-1">
                  <Label className="text-xs">Standort</Label>
                  <Select value={filterLocation} onValueChange={setFilterLocation}>
                    <SelectTrigger className="h-9 text-sm">
                      <SelectValue placeholder="Alle" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle Standorte</SelectItem>
                      {locations.map(l => (
                        <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-1">
                <Label className="text-xs">Schicht</Label>
                <Select value={filterSchicht} onValueChange={setFilterSchicht}>
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue placeholder="Alle" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle Schichten</SelectItem>
                    {schichten.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.kuerzel} · {s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Von</Label>
                <Input
                  type="date"
                  value={filterDatumVon}
                  onChange={e => setFilterDatumVon(e.target.value)}
                  className="h-9 text-sm"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Bis</Label>
                <Input
                  type="date"
                  value={filterDatumBis}
                  onChange={e => setFilterDatumBis(e.target.value)}
                  className="h-9 text-sm"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Protokoll-Liste */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : protokolle.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center">
              <ClipboardCheck className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-muted-foreground font-medium">Noch keine Protokolle vorhanden</p>
              <p className="text-sm text-muted-foreground/70 mt-1">
                Protokolle werden beim Beenden einer Schicht erstellt.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">{protokolle.length} Protokoll{protokolle.length !== 1 ? 'e' : ''}</p>
            {protokolle.map(p => {
              const isExpanded = expandedId === p.id;
              const schichtInfo = p.schicht_info || {};
              return (
                <Card key={p.id} className="shadow-sm" data-testid={`protokoll-${p.id}`}>
                  <CardContent className="p-0">
                    {/* Collapsed view */}
                    <button
                      className="w-full p-4 text-left"
                      onClick={() => setExpandedId(isExpanded ? null : p.id)}
                    >
                      <div className="flex items-center gap-3">
                        {/* Schicht-Badge */}
                        <div className="bg-primary/10 text-primary rounded-lg w-12 h-12 flex items-center justify-center shrink-0">
                          <span className="font-mono text-xs font-bold">{schichtInfo.kuerzel || '?'}</span>
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-foreground">{schichtInfo.name || 'Unbekannte Schicht'}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_STYLES[p.status] || ''}`}>
                              {STATUS_LABELS[p.status] || p.status}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground flex-wrap">
                            <span>{formatDate(p.datum)}</span>
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {formatTime(p.begonnen_um)} – {formatTime(p.beendet_um)}
                            </span>
                            <span className="flex items-center gap-1">
                              <User className="h-3 w-3" />
                              {p.beendet_von_name || '–'}
                            </span>
                          </div>
                        </div>

                        <div className="shrink-0 text-muted-foreground">
                          {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </div>
                      </div>
                    </button>

                    {/* Expanded detail */}
                    {isExpanded && (
                      <div className="border-t border-border px-4 pb-4 pt-3 space-y-4">
                        {/* Notiz */}
                        {p.ubergabe_notiz && (
                          <div className="space-y-1">
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                              <FileText className="h-3.5 w-3.5" /> Übergabe-Notiz
                            </p>
                            <div className="bg-muted/50 rounded-lg p-3 text-sm text-foreground leading-relaxed">
                              {p.ubergabe_notiz}
                            </div>
                          </div>
                        )}

                        {/* Foto */}
                        {p.ubergabe_foto_path && (
                          <div className="space-y-1">
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                              <Camera className="h-3.5 w-3.5" /> Foto-Anhang
                            </p>
                            <img
                              src={`${API}/tasks/ubergabe-foto/${p.ubergabe_foto_path}`}
                              alt="Übergabe-Foto"
                              className="rounded-lg max-h-64 object-cover w-full"
                            />
                          </div>
                        )}

                        {/* Bestätigt von */}
                        {p.bestaetigt_von_name && (
                          <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                            <CheckCircle2 className="h-4 w-4 shrink-0" />
                            <span>
                              Übernommen von <strong>{p.bestaetigt_von_name}</strong> um {formatTime(p.bestaetigt_um)}
                            </span>
                          </div>
                        )}

                        {/* Session-Zeiten */}
                        <div className="grid grid-cols-2 gap-3 text-xs text-muted-foreground">
                          <div>
                            <span className="font-medium">Gestartet:</span> {p.begonnen_von_name} · {formatTime(p.begonnen_um)}
                          </div>
                          <div>
                            <span className="font-medium">Beendet:</span> {p.beendet_von_name} · {formatTime(p.beendet_um)}
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default SchichtProtokoll;
